/**
 * UndoRedoControls component - Undo and redo buttons for sidebar
 * Factory function following the app's architectural patterns
 */

import {
  createIconButton,
  type IconButtonAPI,
  type IconButtonConfig,
} from "@UI/IconButton/index.js";

export interface UndoRedoControlsConfig {
  readonly onUndoClick?: () => void;
  readonly onRedoClick?: () => void;
}

export interface UndoRedoControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setUndoDisabled: (disabled: boolean) => void;
  readonly setRedoDisabled: (disabled: boolean) => void;
  readonly destroy: () => void;
}

/**
 * Creates UndoRedoControls component with undo and redo buttons
 * @param config - Configuration for the undo/redo controls
 * @returns UndoRedoControls API
 */
export const createUndoRedoControls = (
  config: UndoRedoControlsConfig = {}
): UndoRedoControlsAPI => {
  const { onUndoClick, onRedoClick } = config;

  let isDestroyed = false;
  let undoButtonAPI: IconButtonAPI | null = null;
  let redoButtonAPI: IconButtonAPI | null = null;

  // Create container element
  const container = document.createElement("div");
  container.className = "undo-redo-controls";

  /**
   * Initialize the undo/redo controls with two IconButtons
   */
  const initUndoRedoControls = (): void => {
    try {
      // Create undo button
      const undoConfig: IconButtonConfig = {
        icon: "modifiers_undo",
        size: 32,
        iconSize: 24,
        behavior: "button",
        className: "undo-redo-button undo-redo-button--undo",
        ...(onUndoClick
          ? {
              onClick: (_selected?: boolean) => {
                onUndoClick();
              },
            }
          : {}),
      };
      undoButtonAPI = createIconButton(undoConfig);

      // Create redo button
      const redoConfig: IconButtonConfig = {
        icon: "modifiers_redo",
        size: 32,
        iconSize: 24,
        behavior: "button",
        className: "undo-redo-button undo-redo-button--redo",
        ...(onRedoClick
          ? {
              onClick: (_selected?: boolean) => {
                onRedoClick();
              },
            }
          : {}),
      };
      redoButtonAPI = createIconButton(redoConfig);

      // Append buttons to container
      container.appendChild(undoButtonAPI.getElement());
      container.appendChild(redoButtonAPI.getElement());
    } catch (error) {
      console.error("Failed to create UndoRedoControls component:", error);
      throw error;
    }
  };

  /**
   * Set undo button disabled state
   */
  const setUndoDisabled = (disabled: boolean): void => {
    if (isDestroyed || !undoButtonAPI) return;
    undoButtonAPI.setDisabled(disabled);
  };

  /**
   * Set redo button disabled state
   */
  const setRedoDisabled = (disabled: boolean): void => {
    if (isDestroyed || !redoButtonAPI) return;
    redoButtonAPI.setDisabled(disabled);
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy button components
    undoButtonAPI?.destroy();
    redoButtonAPI?.destroy();

    // Clear references
    undoButtonAPI = null;
    redoButtonAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initUndoRedoControls();

  return {
    getElement: () => container,
    setUndoDisabled,
    setRedoDisabled,
    destroy,
  };
};

/**
 * Creates UndoRedoControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the undo/redo controls
 * @returns UndoRedoControls API
 */
export const createUndoRedoControlsInContainer = (
  container: HTMLElement,
  config: UndoRedoControlsConfig = {}
): UndoRedoControlsAPI => {
  const undoRedoControls = createUndoRedoControls(config);
  container.appendChild(undoRedoControls.getElement());
  return undoRedoControls;
};
